import { type ReactElement } from 'react';
import { Check, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import type { FhirIdentifierMappingViewModel } from '../types';

const EMPTY_CELL = '—';

interface IdentifierMappingsTableProps {
  readonly rows: readonly FhirIdentifierMappingViewModel[];
  readonly onEdit: (row: FhirIdentifierMappingViewModel) => void;
  readonly onDelete: (row: FhirIdentifierMappingViewModel) => void;
  /** Guid of the row currently being deleted; that row's controls render disabled. */
  readonly pendingDeleteGuid: string | null;
}

export function IdentifierMappingsTable({
  rows,
  onEdit,
  onDelete,
  pendingDeleteGuid,
}: IdentifierMappingsTableProps): ReactElement {
  const { t } = useTranslation();
  return (
    <table
      className="w-full border-collapse text-sm"
      aria-label={t('routes.admin.fhirIntegrations.mappings.heading')}
    >
      <thead>
        <tr className="border-b border-border text-left text-xs text-muted-foreground">
          <th className="py-2 pr-3 font-medium">
            {t('routes.admin.fhirIntegrations.mappings.table.fhirSystem')}
          </th>
          <th className="py-2 pr-3 font-medium">
            {t('routes.admin.fhirIntegrations.mappings.table.identifier')}
          </th>
          <th className="py-2 pr-3 font-medium">
            {t('routes.admin.fhirIntegrations.mappings.table.label')}
          </th>
          <th className="py-2 pr-3 font-medium">
            {t('routes.admin.fhirIntegrations.mappings.table.patientIdResolution')}
          </th>
          <th className="py-2 pr-3 font-medium">
            {t('routes.admin.fhirIntegrations.mappings.table.active')}
          </th>
          <th className="py-2 text-right font-medium">
            <span className="sr-only">
              {t('routes.admin.fhirIntegrations.mappings.table.actions')}
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isDeleting = pendingDeleteGuid === row.guid;
          return (
            <tr
              key={row.guid}
              className={cn('border-b border-border/60', !row.active && 'opacity-60')}
            >
              <td className="py-2 pr-3 font-mono text-xs">
                {row.fhirSystemValue.length > 0 ? row.fhirSystemValue : EMPTY_CELL}
              </td>
              <td className="py-2 pr-3">
                {row.identifierLabel.length > 0
                  ? t('routes.admin.fhirIntegrations.mappings.table.identifierValue', {
                      label: row.identifierLabel,
                      userEnteredId: row.identifierUserEnteredId,
                    })
                  : EMPTY_CELL}
              </td>
              <td className="py-2 pr-3 text-muted-foreground">
                {row.label.length > 0 ? row.label : EMPTY_CELL}
              </td>
              <td className="py-2 pr-3">
                {row.usedForPatientIdResolution ? (
                  <Check
                    className="h-4 w-4 text-primary"
                    aria-label={t('routes.admin.fhirIntegrations.mappings.table.patientIdResolution')}
                  />
                ) : (
                  <span className="text-muted-foreground" aria-hidden="true">
                    {EMPTY_CELL}
                  </span>
                )}
              </td>
              <td className="py-2 pr-3">
                {row.active ? (
                  <Check
                    className="h-4 w-4 text-primary"
                    aria-label={t('routes.admin.fhirIntegrations.mappings.table.active')}
                  />
                ) : (
                  <span className="text-muted-foreground" aria-hidden="true">
                    {EMPTY_CELL}
                  </span>
                )}
              </td>
              <td className="py-2 text-right">
                <div className="inline-flex gap-0.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      onEdit(row);
                    }}
                    disabled={isDeleting}
                    aria-label={t('routes.admin.fhirIntegrations.mappings.table.editRow', {
                      system: row.fhirSystemValue,
                    })}
                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  >
                    <Pencil className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      onDelete(row);
                    }}
                    disabled={isDeleting}
                    aria-label={t('routes.admin.fhirIntegrations.mappings.table.deleteRow', {
                      system: row.fhirSystemValue,
                    })}
                    className={cn(
                      'h-7 w-7 text-muted-foreground hover:text-destructive',
                      isDeleting && 'opacity-60',
                    )}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
